import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Grid3x3, List, ArrowLeft } from 'lucide-react';
import { checkInOutService } from '@/services/checkinout-service';
import type { CalendarEvent, CalendarView } from '@/types/checkinout';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
import { toast } from 'sonner';

export default function CheckInOutCalendar(): React.ReactElement {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<'asset' | 'category' | 'site'>('asset');
  const [displayMode, setDisplayMode] = useState<'calendar' | 'list'>('calendar');

  useEffect(() => {
    loadCalendarData();
  }, [currentDate, viewType]);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      const view: CalendarView = {
        type: viewType,
        dateRange: {
          from: startOfMonth(currentDate).toISOString(),
          to: endOfMonth(currentDate).toISOString(),
        },
      };
      const data = await checkInOutService.getCalendarEvents(view);
      setEvents(data);
    } catch (error) {
      console.error('Failed to load calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'available': 'bg-green-500',
      'confirmed': 'bg-blue-500',
      'pending': 'bg-yellow-500',
      'checked-out': 'bg-blue-600',
      'due-soon': 'bg-amber-500',
      'overdue': 'bg-red-500',
      'in-transit': 'bg-purple-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  const getEventsForDay = (day: Date) => {
    return events.filter(event => {
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      return day >= start && day <= end;
    });
  };

  const monthDays = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate),
  });

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return (
      <div className="flex-1 space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <CalendarIcon className="h-6 w-6 text-primary" />
              Check-In / Check-Out Calendar
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              View scheduled asset check-ins and check-outs in calendar format
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/checkinout')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Select value={viewType} onValueChange={(value: any) => setViewType(value)}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asset">By Asset</SelectItem>
                <SelectItem value="category">By Category</SelectItem>
                <SelectItem value="site">By Site</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={displayMode === 'calendar' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setDisplayMode('calendar')}
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              variant={displayMode === 'list' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setDisplayMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-6 p-6">

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <CardTitle className="text-xl">
                {format(currentDate, 'MMMM yyyy')}
              </CardTitle>
              <Button variant="outline" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={handleToday}>
                Today
              </Button>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center gap-1">
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                  <span>Available</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-3 w-3 rounded-full bg-blue-500" />
                  <span>Reserved</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-3 w-3 rounded-full bg-amber-500" />
                  <span>Due Soon</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <span>Overdue</span>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {displayMode === 'calendar' ? (
            <div className="grid grid-cols-7 gap-2">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground p-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {monthDays.map(day => {
                const dayEvents = getEventsForDay(day);
                const isToday = isSameDay(day, new Date());
                
                return (
                  <div
                    key={day.toISOString()}
                    className={`min-h-[120px] border rounded-lg p-2 ${
                      isToday ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                  >
                    <div className={`text-sm font-medium mb-2 ${
                      isToday ? 'text-primary' : 'text-foreground'
                    }`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(event => (
                        <div
                          key={event.id}
                          className={`text-xs p-1 rounded truncate ${getStatusColor(event.status)} text-white`}
                          title={`${event.assetName} - ${event.assignedToName}`}
                        >
                          {event.assetName}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-muted-foreground">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              {events.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No events this month</p>
                  <p className="text-sm text-muted-foreground">
                    All assets are available
                  </p>
                </div>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className={`h-10 w-1 rounded-full ${getStatusColor(event.status)}`} />
                    <div className="flex-1">
                      <div className="font-medium">{event.assetName}</div>
                      <div className="text-sm text-muted-foreground">
                        {event.assignedToName} • {event.category}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">
                        {format(new Date(event.startDate), 'dd MMM')} - {format(new Date(event.endDate), 'dd MMM')}
                      </div>
                      <Badge variant={event.status === 'overdue' ? 'destructive' : 'secondary'}>
                        {event.type === 'checkout' ? 'Checked Out' : 'Reserved'}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
